import { isMobileDevice } from '@src/hooks/useIsMobile';
import { Selection } from '@tiptap/pm/state';
import type { Editor } from '@tiptap/react';
import { useCallback, useEffect, useState } from 'react';

import type { LinkModalReturn } from './modals/useLinkModal';

interface UseTiptapLinkInteractionsProps {
  editor: Editor | null;
  linkModal: LinkModalReturn;
  enableLinkHoverTooltip?: boolean; // Enable link hover tooltip
  enableLinkClickModal?: boolean; // Enable link click modal
}

export function useTiptapLinkInteractions({
  editor,
  linkModal,
  enableLinkHoverTooltip = false,
  enableLinkClickModal = false,
}: UseTiptapLinkInteractionsProps) {
  // Link interaction state - managed locally
  const [isLinkHoverTooltipOpen, setIsLinkHoverTooltipOpen] = useState(false);
  const [linkHoverTooltipPosition, setLinkHoverTooltipPosition] = useState({
    top: 0,
    left: 0,
  });
  const [linkHoverTooltipUrl, setLinkHoverTooltipUrl] = useState('');
  const [isLinkEditPopupOpen, setIsLinkEditPopupOpen] = useState(false);
  // Handle link click to open edit modal
  const handleLinkClick = useCallback(
    (event: MouseEvent) => {
      if (!editor || !enableLinkClickModal) {
        return;
      }

      const target = event.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor || anchor.classList.contains('mention')) {
        return;
      }

      event.preventDefault();
      const url = anchor.getAttribute('href') || '';
      const text = anchor.textContent || '';

      linkModal.setLinkAddModalParams({
        url: url || '',
        text: text || '',
      });

      // SPの場合、タップではフォーカス移動せずに、EditorのLinkが選択されない
      // そのため、フォーカス位置をタップした要素に移動させて、Linkが選択されるようにする
      if (!editor?.getAttributes('link').href) {
        try {
          const pos = editor.view.posAtDOM(anchor, 0);
          if (pos >= 0) {
            const $pos = editor.view.state.doc.resolve(pos);
            const selection = Selection.near($pos);
            editor.view.dispatch(editor.view.state.tr.setSelection(selection));
          }
        } catch (error) {
          // Fallback if position calculation fails
          console.warn('Failed to set cursor position:', error);
        }
      }

      // エディタのフォーカスを外す
      if (editor?.view?.dom) {
        (editor.view.dom as HTMLElement).blur();
      } else {
        document.body.focus();
      }

      setIsLinkEditPopupOpen(true);
    },
    [editor, linkModal, enableLinkClickModal],
  );

  // Handle link hover to show tooltip
  const handleLinkHover = useCallback(
    (event: MouseEvent) => {
      if (!editor || !enableLinkHoverTooltip) {
        return;
      }

      if (isMobileDevice()) {
        return;
      }

      const TOOLTIP_OFFSET = {
        BASE: 15,
        MAX: 80,
        COEFFICIENT: 0.5, // URLの長さに対するオフセットの係数
      };

      const target = event.target as HTMLElement;
      const anchor = target.closest('a');
      if (!anchor || anchor.classList.contains('mention')) {
        return;
      }

      const container = document.querySelector('.ProseMirror') as HTMLElement | null;
      if (!container) {
        return;
      }

      const rect = anchor.getBoundingClientRect();
      const url = anchor.getAttribute('href') || '';
      const tooltipOffset =
        TOOLTIP_OFFSET.BASE + Math.min(url.length * TOOLTIP_OFFSET.COEFFICIENT, TOOLTIP_OFFSET.MAX);
      const containerRect = container.getBoundingClientRect();

      setLinkHoverTooltipUrl(url);
      setLinkHoverTooltipPosition({
        top: rect.bottom - containerRect.top - tooltipOffset,
        left: rect.left - containerRect.left,
      });
      setIsLinkHoverTooltipOpen(true);
    },
    [editor, enableLinkHoverTooltip],
  );

  // Handle mouse leave to hide tooltip
  const handleMouseOut = useCallback(() => {
    if (!enableLinkHoverTooltip) {
      return;
    }
    setIsLinkHoverTooltipOpen(false);
  }, [enableLinkHoverTooltip]);

  useEffect(() => {
    if (!editor || (!enableLinkHoverTooltip && !enableLinkClickModal)) {
      return;
    }

    const editorDom = editor.view.dom;

    if (enableLinkClickModal) {
      editorDom.addEventListener('click', handleLinkClick);
    }

    if (enableLinkHoverTooltip) {
      editorDom.addEventListener('mouseover', handleLinkHover);
      editorDom.addEventListener('mouseout', handleMouseOut);
    }

    return () => {
      if (enableLinkClickModal) {
        editorDom.removeEventListener('click', handleLinkClick);
      }
      if (enableLinkHoverTooltip) {
        editorDom.removeEventListener('mouseover', handleLinkHover);
        editorDom.removeEventListener('mouseout', handleMouseOut);
      }
    };
  }, [
    editor,
    handleLinkClick,
    handleLinkHover,
    handleMouseOut,
    enableLinkHoverTooltip,
    enableLinkClickModal,
  ]);

  return {
    // Event handlers
    handleLinkClick,
    handleLinkHover,
    handleMouseOut,
    // Interaction state
    isLinkHoverTooltipOpen,
    linkHoverTooltipPosition,
    linkHoverTooltipUrl,
    isLinkEditPopupOpen,
    // State setters (for event handlers)
    setIsLinkHoverTooltipOpen,
    setLinkHoverTooltipPosition,
    setLinkHoverTooltipUrl,
    setIsLinkEditPopupOpen,
  };
}
